import { useCallback, useEffect, useRef, useState } from "react";
import MapComponent from "../components/map/mapComponent";
import Navbar from "../components/navigation/Navbar";
import { VideoPlayerSection } from "../components/video_player/VideoPlayerSection";
import { CameraViewpointsSection } from "../components/video_player/CameraViewpointsSection";
import { PlaylistSection } from "../components/video_player/PlaylistSection";
import { useRegions } from "../context/RegionsContext";
import { useVideoPlaylist } from "../hooks/useVideoPlaylist";
import { useCameraViewpoints } from "../hooks/useCameraViewpoints";

const VideoPlayerApp = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(745);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [selectedSystemId, setSelectedSystemId] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);

  const { allSystems, selectedRegion, isLoading } = useRegions();
  const videoRef = useRef(null);

  // Custom hooks
  const {
    playlist,
    playlistLoading,
    playlistError,
    allFilesData,
    fetchPlaylist,
    getFileUrl,
  } = useVideoPlaylist(selectedRegion);

  const {
    cameraViewpoints,
    setCameraViewpoints,
    cameraViewpointsLoading,
    fetchCameraViewpoints,
  } = useCameraViewpoints();

  // Utility function for thumbnail colors
  const getThumbnailColor = useCallback((index) => {
    const colors = [
      "from-amber-600 to-amber-700",
      "from-emerald-600 to-emerald-700",
      "from-indigo-600 to-indigo-700",
      "from-purple-600 to-purple-700",
      "from-red-600 to-red-700",
      "from-blue-600 to-blue-700",
      "from-pink-600 to-pink-700",
      "from-cyan-600 to-cyan-700",
    ];
    return colors[index % colors.length];
  }, []);

  // Handler for camera viewpoint selection
  const handleCameraViewpointSelect = useCallback((viewpointId) => {
    setVideoLoading(true);
    setSelectedVideoId(viewpointId);
    setCurrentTime(0);
    setTimeout(() => setVideoLoading(false), 100);
  }, []);

  // Handler for playlist item selection
  const handlePlaylistItemSelect = useCallback(
    (videoId) => {
      setVideoLoading(true);
      setSelectedVideoId(videoId);
      setCurrentTime(0);
      const video = allFilesData.find((v) => v.id === videoId);
      if (video) {
        fetchCameraViewpoints(video, allFilesData);
      }
      setTimeout(() => setVideoLoading(false), 100);
    },
    [allFilesData, fetchCameraViewpoints]
  );

  // Handler for filters applied
  const handleFiltersApplied = useCallback(
    async (filters) => {
      setAppliedFilters(filters);
      if (filters) {
        setSelectedSystemId(filters.systemId);
        const newPlaylist = await fetchPlaylist(filters);
        
        // Auto-select first video if available
        if (newPlaylist && newPlaylist.length > 0) {
          setSelectedVideoId(newPlaylist[0].id);
        }
      } else {
        setSelectedSystemId(null);
        setSelectedVideoId(null);
        setCameraViewpoints([])
      }
    },
    [fetchPlaylist]
  );

  // Effect to fetch camera viewpoints when playlist changes
  useEffect(() => {
    if (playlist.length > 0 && selectedVideoId) {
      const video = allFilesData.find((v) => v.id === selectedVideoId);
      if (video) {
        fetchCameraViewpoints(video, allFilesData);
      }
    }
  }, [playlist, selectedVideoId, allFilesData, fetchCameraViewpoints]);

  // Video playback effect
  useEffect(() => {
    let interval;
    if (isPlaying && currentTime < duration) {
      interval = setInterval(() => setCurrentTime((prev) => prev + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, duration]);

  return (
    <>
      <Navbar />
      <div className="pt-14">
        <div className="min-h-screen p-4 md:p-8">
          <div className="h-[calc(100vh-120px)]">
            <div className="grid grid-cols-3 gap-4 h-full">
              {/* Left Column - Video Player & Camera Viewpoints */}
              <div className="col-span-2 grid grid-rows-[2fr_1fr] gap-4">
                <VideoPlayerSection
                  appliedFilters={appliedFilters}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  currentTime={currentTime}
                  setCurrentTime={setCurrentTime}
                  duration={duration}
                  selectedVideo={allFilesData.find((v) => v.id === selectedVideoId)}
                  getFileUrl={getFileUrl}
                  videoRef={videoRef}
                  videoLoading={videoLoading}
                />

                <CameraViewpointsSection
                  cameraViewpoints={cameraViewpoints}
                  cameraViewpointsLoading={cameraViewpointsLoading}
                  selectedVideoId={selectedVideoId}
                  handleCameraViewpointSelect={handleCameraViewpointSelect}
                  getFileUrl={getFileUrl}
                />
              </div>

              {/* Right Column - Playlist/Filters & Map */}
              <div className="col-span-1 grid grid-rows-[2fr_1fr] gap-4">
                <PlaylistSection
                  showPlaylist={showPlaylist}
                  setShowPlaylist={setShowPlaylist}
                  playlistLoading={playlistLoading}
                  playlistError={playlistError}
                  playlist={playlist}
                  selectedVideoId={selectedVideoId}
                  handlePlaylistItemSelect={handlePlaylistItemSelect}
                  getThumbnailColor={getThumbnailColor}
                  getFileUrl={getFileUrl}
                  allSystems={allSystems}
                  isLoading={isLoading}
                  onFiltersApplied={handleFiltersApplied}
                />

                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <MapComponent
                    selectedSystemId={selectedSystemId}
                    appliedFilters={appliedFilters}
                    className="h-full w-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VideoPlayerApp;
